// src/components/TrialOfferModal.js
//
// The "your trial is nearly up" offer, shown on the dashboard.
//
// Two things happen here, and they're separable on purpose:
//
//   1. EXTEND — one click, no card, adds free months. Granted by the
//      extendTrial callable, never by the client (see functions/trialOffers.js
//      for why).
//
//   2. UPGRADE AT HALF PRICE — sends them to Stripe Checkout with the
//      promotion code pre-applied AND the extra free months attached as a
//      Stripe trial, so "buy now and you still get the free time" is true
//      rather than marketing.
//
// Someone who takes (2) never needs (1), and taking (1) doesn't disqualify
// them from (2) later. Handling them as one button is what makes these offers
// feel like a trap.

import React, { useState } from "react";
import { getFunctions, httpsCallable } from "firebase/functions";
import { Link } from "react-router-dom";

export default function TrialOfferModal({
  open,
  daysRemaining,
  alreadyExtended = false,
  offerMonths = 2,
  discountLabel = "50% off your first year",
  onExtended,
  onDismiss,
}) {
  const [busy, setBusy] = useState("");
  const [error, setError] = useState("");
  const [acceptedTerms, setAcceptedTerms] = useState(false);

  if (!open) return null;

  const functions = getFunctions();

  const handleExtend = async () => {
    setBusy("extend");
    setError("");
    try {
      const extendTrial = httpsCallable(functions, "extendTrial");
      const res = await extendTrial({ months: offerMonths });
      onExtended?.(res.data);
    } catch (err) {
      console.error("[TrialOfferModal] extend failed:", err);
      setError(
        err?.message ||
          "We couldn't extend your trial. Please try again, or call (571) 563-6142."
      );
    } finally {
      setBusy("");
    }
  };

  const handleUpgrade = async () => {
    if (!acceptedTerms) {
      setError("Please confirm you've read the Terms & Conditions.");
      return;
    }
    setBusy("upgrade");
    setError("");
    try {
      const createCheckoutSession = httpsCallable(
        functions,
        "createCheckoutSession"
      );
      const res = await createCheckoutSession({
        planKey: "educator_yearly",
        // Free months ride along as a Stripe trial, so the promise on this
        // screen is the one Stripe actually honours.
        bonusTrialMonths: alreadyExtended ? 0 : offerMonths,
        applyRetentionDiscount: true,
        acceptedTerms: true,
        successUrl: `${window.location.origin}/upgrade-success`,
        cancelUrl: window.location.href,
      });
      if (res?.data?.url) window.location.assign(res.data.url);
      else throw new Error("No checkout URL returned.");
    } catch (err) {
      console.error("[TrialOfferModal] checkout failed:", err);
      setError(
        err?.message ||
          "We couldn't open checkout. Please try again, or call (571) 563-6142."
      );
      setBusy("");
    }
  };

  const dayLabel =
    daysRemaining === null || daysRemaining === undefined
      ? "soon"
      : daysRemaining <= 0
      ? "today"
      : daysRemaining === 1
      ? "tomorrow"
      : `in ${daysRemaining} days`;

  return (
    <div style={s.overlay} role="dialog" aria-modal="true" aria-label="Trial offer">
      <div style={s.card}>
        <button style={s.close} onClick={onDismiss} aria-label="Close">
          ×
        </button>

        <p style={s.eyebrow}>Your trial ends {dayLabel}</p>
        <h2 style={s.title}>
          {alreadyExtended
            ? "Keep your classroom running"
            : `Have ${offerMonths} more months, on us`}
        </h2>

        <p style={s.body}>
          {alreadyExtended
            ? "Your students' progress, scores and avatars stay exactly as they are. Lock in your rate before your trial closes."
            : `No card needed. Your roster, your students' scores and everything you've set up stays in place — and you'll still get ${discountLabel.toLowerCase()} whenever you're ready to subscribe.`}
        </p>

        {!alreadyExtended && (
          <button
            style={{ ...s.primary, opacity: busy ? 0.6 : 1 }}
            disabled={Boolean(busy)}
            onClick={handleExtend}
          >
            {busy === "extend"
              ? "Extending…"
              : `Extend my free trial by ${offerMonths} months`}
          </button>
        )}

        <div style={s.divider}>
          <span style={s.dividerText}>or subscribe now and save</span>
        </div>

        <div style={s.offerBox}>
          <p style={s.offerHeadline}>{discountLabel}</p>
          <p style={s.offerSub}>
            {alreadyExtended
              ? "Billing starts at the end of your extended trial."
              : `Your ${offerMonths} free months are included — billing starts after they're up.`}
          </p>
        </div>

        {/* Terms acceptance is captured at every payment entry point, not
            just registration. See components/legal/TermsAcceptance.js. */}
        <label style={s.termsRow}>
          <input
            type="checkbox"
            checked={acceptedTerms}
            onChange={(e) => setAcceptedTerms(e.target.checked)}
            style={s.checkbox}
          />
          <span style={s.termsText}>
            I have read and agree to the{" "}
            <Link to="/terms-of-service" target="_blank" style={s.link}>
              Terms &amp; Conditions
            </Link>
            , including that{" "}
            <strong>all payments are non-refundable</strong>.
          </span>
        </label>

        <button
          style={{
            ...s.secondary,
            opacity: busy || !acceptedTerms ? 0.6 : 1,
          }}
          disabled={Boolean(busy) || !acceptedTerms}
          onClick={handleUpgrade}
        >
          {busy === "upgrade" ? "Opening checkout…" : "Subscribe at half price"}
        </button>

        {error && <p style={s.error}>{error}</p>}

        <button style={s.dismiss} onClick={onDismiss}>
          Remind me later
        </button>
      </div>
    </div>
  );
}

const s = {
  overlay: {
    position: "fixed",
    inset: 0,
    background: "rgba(15,23,42,0.6)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: 20,
    zIndex: 1200,
  },
  card: {
    position: "relative",
    background: "#fff",
    borderRadius: 16,
    padding: "34px 30px 26px",
    width: "100%",
    maxWidth: 480,
    maxHeight: "90vh",
    overflowY: "auto",
    boxShadow: "0 24px 60px rgba(15,23,42,0.28)",
    fontFamily:
      "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', system-ui, sans-serif",
  },
  close: {
    position: "absolute",
    top: 12,
    right: 14,
    background: "none",
    border: "none",
    fontSize: 26,
    lineHeight: 1,
    color: "#94a3b8",
    cursor: "pointer",
  },
  eyebrow: {
    margin: 0,
    fontSize: 12,
    fontWeight: 700,
    letterSpacing: "0.08em",
    textTransform: "uppercase",
    color: "#b45309",
  },
  title: {
    margin: "6px 0 10px",
    fontSize: 26,
    fontWeight: 800,
    lineHeight: 1.2,
    color: "#0f172a",
  },
  body: { margin: "0 0 20px", color: "#475569", fontSize: 15, lineHeight: 1.55 },
  primary: {
    width: "100%",
    background: "#1d4ed8",
    color: "#fff",
    border: "none",
    borderRadius: 10,
    padding: "14px 18px",
    fontSize: 15,
    fontWeight: 700,
    cursor: "pointer",
  },
  secondary: {
    width: "100%",
    background: "#0f172a",
    color: "#fff",
    border: "none",
    borderRadius: 10,
    padding: "14px 18px",
    fontSize: 15,
    fontWeight: 700,
    cursor: "pointer",
    marginTop: 4,
  },
  divider: {
    display: "flex",
    alignItems: "center",
    textAlign: "center",
    margin: "20px 0 16px",
    color: "#94a3b8",
  },
  dividerText: {
    fontSize: 12,
    fontWeight: 600,
    textTransform: "uppercase",
    letterSpacing: "0.06em",
    margin: "0 auto",
    background: "#fff",
    padding: "0 10px",
  },
  offerBox: {
    background: "#f0fdf4",
    border: "1px solid #bbf7d0",
    borderRadius: 10,
    padding: "14px 16px",
    marginBottom: 16,
  },
  offerHeadline: {
    margin: 0,
    fontSize: 17,
    fontWeight: 800,
    color: "#166534",
  },
  offerSub: { margin: "3px 0 0", fontSize: 13, color: "#15803d" },
  termsRow: {
    display: "flex",
    gap: 10,
    alignItems: "flex-start",
    margin: "0 0 14px",
    cursor: "pointer",
  },
  checkbox: { marginTop: 3, width: 16, height: 16, flexShrink: 0 },
  termsText: { fontSize: 13, color: "#475569", lineHeight: 1.5 },
  link: { color: "#1d4ed8", fontWeight: 600 },
  error: {
    margin: "12px 0 0",
    background: "#fef2f2",
    border: "1px solid #fecaca",
    color: "#991b1b",
    borderRadius: 8,
    padding: "10px 12px",
    fontSize: 13,
  },
  dismiss: {
    display: "block",
    margin: "14px auto 0",
    background: "none",
    border: "none",
    color: "#64748b",
    fontSize: 13,
    fontWeight: 600,
    cursor: "pointer",
  },
};
